import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Ticket } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useNotificationCount } from '@/hooks/useNotificationCount';
import { useCreateSheet } from '@/context/CreateSheetContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { OnlineNow, TrendingPeople } from '@/components/community';

// Primary nav — 5-tab architecture (mirrors bottom nav)
const PRIMARY_NAV = [
  { icon: '⬛', label: 'The Block',   href: '/home'             },
  { icon: '🍽',  label: 'The Cookout', href: '/home?tab=cookout', cookout: true },
  { icon: '💬',  label: 'The GC',     href: '/gc',              badge: true },
  { icon: '🔍',  label: 'Explore',    href: '/explore'          },
];

const SECONDARY_NAV = [
  { icon: '✧', label: 'Bonita', href: '/bonita' },
];

// Always visible — no toggle
const MORE_NAV = [
  { icon: '↑', label: 'The Word',         href: '/word'                },
  { icon: '⟳', label: 'The Vouch',        href: '/vouch'               },
  { icon: '◈', label: 'Culture Calendar', href: '/calendar'            },
];

const NavItem = ({ item, isActive, unreadCount, isVouched }) => (
  <Link
    to={item.href}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '9px 10px',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all .14s',
      color: isActive ? '#F07B1D' : '#72788A',
      background: isActive ? 'rgba(240,123,29,0.08)' : 'transparent',
      border: isActive ? '1px solid rgba(240,123,29,0.2)' : '1px solid transparent',
      fontSize: '14px',
      fontWeight: 500,
      textDecoration: 'none',
      marginBottom: '1px',
    }}
  >
    <span style={{ width: '18px', textAlign: 'center', fontSize: '14px', flexShrink: 0 }}>
      {item.icon}
    </span>
    <span style={{ flex: 1 }}>{item.label}</span>

    {item.badge && unreadCount > 0 && (
      <span style={{
        marginLeft: 'auto',
        background: '#D44F2E',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: '8px',
      }}>
        {unreadCount}
      </span>
    )}

    {item.cookout && (
      isVouched
        ? <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#18B574', flexShrink: 0 }} />
        : <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1px', color: '#3A3F4E' }}>
            🔒 invite
          </span>
    )}
  </Link>
);

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const { count: unreadCount } = useNotificationCount();
  const { open: openCreateSheet } = useCreateSheet();

  const isVouched = user?.is_vouched || user?.is_admin;

  const handleLogout = async () => {
    await logout();
  };

  const borderClass = isDark ? 'border-white/10' : 'border-black/10';
  const bgClass = isDark ? 'bg-black' : 'bg-white';

  const isActive = (href) => {
    if (href.includes('?')) {
      const [path, qs] = href.split('?');
      const param = qs.split('=')[1];
      return location.pathname === path && location.search.includes(param);
    }
    return location.pathname === href;
  };

  return (
    <aside className={`hidden md:flex fixed left-0 top-0 bottom-0 w-64 flex-col border-r ${borderClass} ${bgClass} z-40`}>
      {/* Logo */}
      <div className={`px-5 pt-5 pb-4 border-b ${borderClass}`}>
        <Link to="/home" data-testid="sidebar-logo" className="block">
          <div className="sidebar-logo-wordmark">
            BL<span className="sidebar-logo-vx">VX</span>
          </div>
          <div className="sidebar-logo-tagline">Culture First</div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 pt-2 flex flex-col overflow-y-auto">

        {/* Primary nav */}
        {PRIMARY_NAV.map((item) => (
          <NavItem
            key={item.href}
            item={item}
            isActive={isActive(item.href)}
            unreadCount={unreadCount}
            isVouched={isVouched}
          />
        ))}

        {/* Divider */}
        <hr style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '8px 8px', border: 'none' }} />

        {/* Secondary nav */}
        {SECONDARY_NAV.map((item) => (
          <NavItem
            key={item.href}
            item={item}
            isActive={isActive(item.href)}
            unreadCount={0}
            isVouched={isVouched}
          />
        ))}

        {/* MORE section label */}
        <div style={{
          fontFamily: "'Azeret Mono', monospace",
          fontSize: '8px',
          letterSpacing: '3px',
          color: '#3A3F4E',
          padding: '10px 10px 4px',
          textTransform: 'uppercase',
        }}>
          More
        </div>

        {/* More items — always visible */}
        {MORE_NAV.map((item) => (
          <NavItem
            key={item.href}
            item={item}
            isActive={isActive(item.href)}
            unreadCount={0}
            isVouched={isVouched}
          />
        ))}

        {/* Community Widgets */}
        <div className={`mt-5 pt-4 border-t ${borderClass} space-y-6 px-1 pb-2`}>
          <OnlineNow limit={5} />
          <TrendingPeople limit={5} />
        </div>
      </nav>

      {/* Drop Something CTA */}
      <div className={`px-4 py-3 border-t ${borderClass}`}>
        <button
          onClick={openCreateSheet}
          data-testid="sidebar-drop-btn"
          style={{
            background: '#F07B1D',
            color: '#050608',
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '17px',
            letterSpacing: '2px',
            padding: '13px 18px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all .2s',
            boxShadow: '0 4px 28px rgba(240,123,29,.3), inset 0 1px 0 rgba(255,255,255,.12)',
          }}
        >
          <span style={{ fontSize: '20px', lineHeight: 1 }}>+</span>
          Drop Something
        </button>
      </div>

      {/* User section */}
      {user && (
        <div className={`p-4 border-t ${borderClass}`}>
          <div className="flex items-center gap-2 text-xs text-white/40 mb-4 px-2">
            <Ticket className="h-4 w-4" />
            <span>{user.plates_remaining ?? 10} Plates</span>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <Avatar className={`h-10 w-10 border ${borderClass}`}>
              <AvatarImage
                src={user.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${user.name || 'U'}&backgroundColor=1a1a1a&textColor=ffffff`}
                alt={user.name || 'User'}
              />
              <AvatarFallback className="bg-white/10 text-white text-sm">
                {(user.name || user.username || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white text-sm truncate">{user.name || 'User'}</p>
              <p className="text-xs text-white/40 truncate">@{user.username || 'user'}</p>
            </div>
          </div>

          <Button
            variant="ghost"
            className="w-full justify-start text-white/40 hover:text-white hover:bg-white/5 text-sm"
            onClick={handleLogout}
            data-testid="sidebar-logout"
          >
            <LogOut className="mr-3 h-4 w-4" />
            Log out
          </Button>
        </div>
      )}
    </aside>
  );
};
